import React, { useEffect, useRef, useState } from 'react';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';

interface ModernMarkerProps {
    position: { lat: number; lng: number };
    title?: string;
    icon?: string;
    onMouseOver?: () => void;
    onMouseOut?: () => void;
    onClick?: () => void;
    map?: google.maps.Map | null;
}

const ModernMarker: React.FC<ModernMarkerProps> = ({
    position,
    title,
    icon,
    onMouseOver,
    onMouseOut,
    onClick,
    map
}) => {
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const pinElementRef = useRef<HTMLElement | null>(null);
    const { isMarkerLibraryLoaded } = useGoogleMaps();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (!isMarkerLibraryLoaded || !map) {
            return;
        }

        const initializeMarker = async () => {
            try {
                // Import the marker library
                const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

                // Create pin element if icon is provided
                if (icon && !pinElementRef.current) {
                    const pinElement = new PinElement({
                        background: '#4285F4',
                        borderColor: '#137333',
                        glyphColor: '#ffffff',
                        scale: 1.2,
                    });
                    pinElementRef.current = pinElement.element;
                }

                // Create the marker
                markerRef.current = new AdvancedMarkerElement({
                    position,
                    map,
                    title,
                    content: icon ? pinElementRef.current : undefined,
                });

                // Add event listeners
                if (onMouseOver) {
                    markerRef.current.addListener('mouseover', onMouseOver);
                }
                if (onMouseOut) {
                    markerRef.current.addListener('mouseout', onMouseOut);
                }
                if (onClick) {
                    markerRef.current.addListener('click', onClick);
                }

                console.log('✅ Modern marker created successfully at position:', position);
                setIsInitialized(true);
            } catch (error) {
                console.error('❌ Error creating modern marker:', error);
            }
        };

        initializeMarker();

        // Cleanup function
        return () => {
            if (markerRef.current) {
                markerRef.current.map = null;
                markerRef.current = null;
            }
        };
    }, [isMarkerLibraryLoaded, map, position.lat, position.lng, title, icon, onMouseOver, onMouseOut, onClick]);

    // Update marker position when position changes
    useEffect(() => {
        if (markerRef.current && isInitialized) {
            markerRef.current.position = position;
        }
    }, [position.lat, position.lng, isInitialized]);

    return null; // This component doesn't render anything visible
};

export default ModernMarker;
